import { LinkPreview } from "@/src/models/link-preview.model";
import { useTheme } from "@/src/styles/theme-provider";
import { Link21 } from "iconsax-react-nativejs";
import { Linking, Pressable, Text, View } from "react-native";
import PreviewDismissButton from "./dismiss-button";
import PreviewFooter from "./preview-footer";
import PreviewImage from "./preview-image";

export type PreviewCardProps = Readonly<{
  preview: LinkPreview;
  /** Composer: a shorter image, like the Selisian composer card. */
  compact?: boolean;
  /** Composer: shows an × that removes the card. */
  onDismiss?: () => void;
}>;

/**
 * Open Graph card for an ordinary web page: image, title, description, and a
 * footer with the site's favicon, name and domain (same layout as the Selisian app).
 *
 * Every field is optional — a page with only a `<title>` gets a title and a
 * footer, a page with no `og:image` gets a text-only card. The whole card is
 * one press target, opening the link the same way the URL in the post body
 * does.
 */
const WebpagePreview = ({ preview, compact, onDismiss }: PreviewCardProps) => {
  const { themeVars } = useTheme();
  const slate = themeVars["--color-slate-default"] as string;

  return (
    <Pressable
      onPress={() => {
        Linking.openURL(preview.url).catch(() => {
          // A blocked pop-up or unsupported scheme is not worth a crash.
        });
      }}
      className="mx-4 mb-2 border border-ash-3 rounded-xl overflow-hidden bg-chalk hover:bg-ash-3 active:opacity-80"
      testID="link-preview-card"
      accessibilityRole="link"
      accessibilityLabel={preview.title ?? preview.domain}
    >
      {/* 1.91:1 is the ratio Open Graph images are authored for. */}
      <PreviewImage
        uri={preview.imageUrl}
        aspectRatio={compact ? 2.4 : 1.91}
        testID="link-preview-image"
        fallback={<Link21 size={24} color={slate} />}
      />
      <View className="px-3 pt-2.5 pb-3 gap-1">
        {preview.title ? (
          <Text className="text-sm font-semibold text-onyx" numberOfLines={2}>
            {preview.title}
          </Text>
        ) : null}
        {preview.description ? (
          <Text className="text-xs text-slate-default" numberOfLines={2}>
            {preview.description}
          </Text>
        ) : null}
        <View className="mt-0.5">
          <PreviewFooter preview={preview} iconColor={slate} />
        </View>
      </View>
      {onDismiss ? <PreviewDismissButton onPress={onDismiss} /> : null}
    </Pressable>
  );
};

export default WebpagePreview;
